import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Tab, Tabs } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/Scheduler.css";
import LocationSalesPanel from "./LocationSalesPanel";

const pad = value => String(value).padStart(2, "0");
const iso = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const firstDayOfMonth = () => {
  const now = new Date();
  return iso(new Date(now.getFullYear(), now.getMonth(), 1));
};
const today = () => iso(new Date());
const lastDayOfMonth = () => {
  const now = new Date();
  return iso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};
const number = (value, digits = 2) => value === null || value === undefined ? "—" : Number(value).toLocaleString("pl-PL", { minimumFractionDigits: digits, maximumFractionDigits: digits });
const money = value => value === null || value === undefined ? "—" : `${number(value)} zł`;
const hours = value => value === null || value === undefined ? "—" : `${number(value)} h`;
const percent = value => value === null || value === undefined ? "—" : `${number(value)}%`;
const signedHours = value => value === null || value === undefined ? "—" : `${Number(value) > 0 ? "+" : ""}${number(value)} h`;
const balanceClass = value => Number(value) < 0 ? "text-success fw-bold" : Number(value) > 0 ? "text-danger fw-bold" : "fw-bold";
const dateTime = value => value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "—";
const getErrorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const rateSource = value => ({
  MANUAL: "Ręczna",
  EMPLOYEE_POSITION_PERIOD: "Stawka okresowa pracownik + stanowisko",
  EMPLOYEE_POSITION: "Stawka domyślna pracownik + stanowisko",
  CONTRACT: "Umowa godzinowa",
  CONTRACT_MONTHLY: "Umowa miesięczna / norma",
  POSITION: "Stawka stanowiska"
}[value] || value || "—");

const insuranceProfile = value => ({
  STUDENT_PUPIL: "Uczeń/student < 26",
  KRUS: "KRUS",
  STANDARD_ZUS: "ZUS",
  NO_ZUS: "Bez składek",
  UNKNOWN: "Nieustalony"
}[value] || value || "—");

const contractPolicy = value => ({
  EMPLOYMENT: "Umowa o pracę",
  CIVIL: "Umowa cywilnoprawna",
  OTHER: "Inna",
  MIXED: "Mieszana",
  NO_CONTRACT: "Brak umowy"
}[value] || value || "—");

const ScheduleManagerReports = () => {
  const axiosPrivate = useAxiosPrivate();
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axiosPrivate.get("/schedule-reports/departments");
        const items = response.data || [];
        setDepartments(items);
        setDepartmentId(current => current || (items.length ? String(items[0].id) : ""));
      } catch (error) {
        setDepartments([]);
        toastError(getErrorMessage(error));
      }
    };
    load();
  }, [axiosPrivate]);

  useEffect(() => {
    const loadEmployees = async () => {
      setEmployeeId("");
      if (!departmentId) {
        setEmployees([]);
        return;
      }
      try {
        const response = await axiosPrivate.get("/schedule-reports/employees", { params: { departmentId } });
        setEmployees(response.data || []);
      } catch (error) {
        setEmployees([]);
        toastError(getErrorMessage(error));
      }
    };
    loadEmployees();
  }, [axiosPrivate, departmentId]);

  const loadReport = async (rangeFrom = dateFrom, rangeTo = dateTo, selectedEmployeeId = employeeId) => {
    if (!departmentId) {
      setReport(null);
      setLoading(false);
      return;
    }
    if (!rangeFrom || !rangeTo || rangeTo < rangeFrom) {
      toastError("Podaj prawidłowy zakres dat.");
      return;
    }
    setLoading(true);
    try {
      const params = { departmentId, dateFrom: rangeFrom, dateTo: rangeTo };
      if (selectedEmployeeId) params.employeeId = selectedEmployeeId;
      const response = await axiosPrivate.get("/schedule-reports/overview", { params });
      setReport(response.data);
    } catch (error) {
      setReport(null);
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(dateFrom, dateTo, "");
  }, [axiosPrivate, departmentId]);

  const summary = report?.summary || {};
  const problemEmployees = useMemo(
    () => (report?.employees || []).filter(row => row.weeksOverFiveDays > 0 || row.weeksUnderThreeDays > 0 || row.weeksUnderFourDays > 0 || row.missingRcpCount > 0 || row.earlyLeaveCount > 0 || (row.underNormHours || 0) > 0),
    [report]
  );
  const mostAbsentEmployee = useMemo(() => {
    const rows = (report?.employees || []).filter(row => (row.absenceDays || 0) > 0);
    return rows.reduce((current, row) => !current || (row.absenceDays || 0) > (current.absenceDays || 0) ? row : current, null);
  }, [report]);
  const averageStaffing = useMemo(() => {
    const days = report?.days || [];
    if (!days.length) return { planned: null, effective: null };
    return {
      planned: days.reduce((sum, row) => sum + Number(row.assignedCount || 0), 0) / days.length,
      effective: days.reduce((sum, row) => sum + Number(row.effectiveAssignedCount || 0), 0) / days.length
    };
  }, [report]);
  const actualHoursValue = Number(summary.actualHours || 0);
  const averageActualNetRate = actualHoursValue > 0 && (summary.incompleteNetRateAssignmentCount || 0) === 0
    ? Number(summary.actualNetWages || 0) / actualHoursValue
    : null;
  const averageActualGrossRate = actualHoursValue > 0 && (summary.incompleteGrossRateAssignmentCount || 0) === 0
    ? Number(summary.actualBaseWages || 0) / actualHoursValue
    : null;

  const useTodayRange = () => {
    const value = today();
    setDateFrom(value);
    setDateTo(value);
    loadReport(value, value);
  };

  const useCurrentMonthRange = () => {
    const from = firstDayOfMonth();
    const to = lastDayOfMonth();
    setDateFrom(from);
    setDateTo(to);
    loadReport(from, to);
  };

  const exportExcel = () => {
    if (!report) return;
    const wb = XLSX.utils.book_new();
    const dashboardRows = [
      { Metryka: "Godziny plan / wykonanie", Wartość: `${number(summary.plannedHours)} / ${number(summary.actualHours)} h` },
      { Metryka: "Bilans plan - wykonanie", Wartość: signedHours(summary.hoursDelta) },
      { Metryka: "Najczęściej nieobecny pracownik", Wartość: mostAbsentEmployee ? `${mostAbsentEmployee.employeeFullName} (${mostAbsentEmployee.absenceDays || 0} dni)` : "Brak nieobecności" },
      { Metryka: "Średnia obsada dzienna plan / efektywna", Wartość: `${number(averageStaffing.planned)} / ${number(averageStaffing.effective)}` },
      { Metryka: "Średnia stawka netto wykonanie", Wartość: averageActualNetRate == null ? "Dane niepełne" : `${number(averageActualNetRate)} zł/h` },
      { Metryka: "Średnia stawka brutto wykonanie", Wartość: averageActualGrossRate == null ? "Dane niepełne" : `${number(averageActualGrossRate)} zł/h` },
      { Metryka: "Najmniej godzin", Wartość: `${summary.minimumHoursEmployee || "—"} (${hours(summary.minimumEmployeeHours)})` },
      { Metryka: "Najwięcej godzin", Wartość: `${summary.maximumHoursEmployee || "—"} (${hours(summary.maximumEmployeeHours)})` },
      { Metryka: "Braki kadrowe", Wartość: summary.effectiveShortageCount || 0 },
      { Metryka: "Osoby wymagające uwagi", Wartość: problemEmployees.length }
    ];
    const employeeRows = (report.employees || []).map(row => ({
      Pracownik: row.employeeFullName,
      "Rodzaj umowy": contractPolicy(row.contractPolicy),
      "Plan h": row.plannedHours,
      "Wykonanie h": row.actualHours,
      "Bilans plan-wykonanie h": row.hoursDelta,
      "Norma etatu h": row.normHours,
      "Bilans do normy h": row.hoursToNorm,
      "Nadgodziny wg normy h": row.overtimeHours,
      "Niedobór do normy h": row.underNormHours,
      "Ponad grafik h": row.shiftExtraHours,
      "Frekwencja %": row.attendancePercent,
      "Dni planowane": row.plannedWorkDays,
      "Dni obecności": row.attendedWorkDays,
      "Dni pracy": row.workingDays,
      "Dni weekendowe": row.weekendDays,
      "Min dni/tydzień": row.minimumDaysInWeek,
      "Max dni/tydzień": row.maximumDaysInWeek,
      "Tygodnie >5 dni": row.weeksOverFiveDays,
      "Tygodnie <3 dni": row.weeksUnderThreeDays,
      "Tygodnie <4 dni": row.weeksUnderFourDays,
      Spóźnienia: row.lateCount,
      "Wcześniejsze wyjścia": row.earlyLeaveCount,
      "Brak RCP": row.missingRcpCount,
      "Dni nieobecności": row.absenceDays,
      "Wynagrodzenie netto plan": row.plannedNetWages,
      "Wynagrodzenie netto wykonanie": row.actualNetWages,
      "Wynagrodzenie brutto plan": row.plannedBaseWages,
      "Wynagrodzenie brutto wykonanie": row.actualBaseWages,
      "Efektywna stawka netto/h": row.effectiveNetHourlyRate,
      "Efektywna stawka brutto/h": row.effectiveGrossHourlyRate,
      "Koszt plan": row.plannedEmployerCost,
      "Koszt wykonanie": row.actualEmployerCost,
      "Planowany koszt odpraw": row.plannedSeveranceCost,
      "Umowy z odprawą w okresie": row.severanceContractCount,
      "Niepełne stawki brutto": row.incompleteGrossRateAssignmentCount,
      "Niepełne stawki netto": row.incompleteNetRateAssignmentCount,
      "Niepełne kalkulacje kosztu": row.incompleteCostAssignmentCount
    }));
    const monthlyRows = (report.monthlyNorms || []).map(row => ({
      Miesiąc: row.month,
      Pracownik: row.employeeFullName,
      Okres: `${row.periodFrom} – ${row.periodTo}`,
      "Pełny miesiąc": row.completeMonth ? "TAK" : "NIE",
      "Rodzaj umowy": contractPolicy(row.contractPolicy),
      "Norma etatu": row.normHours,
      "Plan h": row.plannedHours,
      "Wykonanie h": row.actualHours,
      "Wykonanie etat h": row.employmentActualHours,
      "Bilans do normy h": row.hoursToNorm,
      "Nadgodziny wg normy h": row.overtimeHours,
      "Niedobór do normy h": row.underNormHours,
      "Ponad grafik h": row.shiftExtraHours,
      "Dni planowane": row.plannedWorkDays,
      "Dni obecności": row.attendedWorkDays,
      "Frekwencja %": row.attendancePercent,
      Spóźnienia: row.lateCount,
      "Wcześniejsze wyjścia": row.earlyLeaveCount,
      "Braki RCP": row.missingRcpCount,
      "Dni nieobecności": row.absenceDays
    }));
    const days = (report.days || []).map(row => ({
      Data: row.date,
      "Plan h": row.plannedHours,
      "Wykonanie h": row.actualHours,
      "Bilans dnia h": Number(row.actualHours || 0) - Number(row.plannedHours || 0),
      Zapotrzebowanie: row.demandCount,
      Obsada: row.assignedCount,
      "Braki planowane": row.openShiftCount,
      "Ryzyko obsady RCP": row.rcpCoverageRiskCount,
      "Efektywna obsada": row.effectiveAssignedCount,
      "Braki efektywne": row.effectiveShortageCount,
      "Niepełne dane RCP": row.attendanceDataRiskCount,
      "Minuty zagrożone": row.coverageRiskMinutes,
      Spóźnienia: row.lateCount,
      "Wcześniejsze wyjścia": row.earlyLeaveCount,
      "Brak rozpoczęcia RCP": row.missingRcpCount,
      Wnioski: row.requestCount,
      "Prośby o wolne": row.dayOffRequests,
      Urlopy: row.vacationRequests,
      "Otwarte zmiany": row.openShiftRequests,
      Zamiany: row.swapRequests,
      "Sprzedaż plan": row.plannedSales,
      "Sprzedaż wykonanie": row.actualSales,
      "SPMH plan": row.spmhPlan,
      "SPMH wykonanie": row.spmhActual,
      "%COL plan": row.colPlanPercent,
      "%COL wykonanie": row.colActualPercent,
      "Niepełne kalkulacje kosztu": row.incompleteCostAssignmentCount
    }));
    const rates = (report.rates || []).map(row => ({
      Pracownik: row.employeeFullName,
      Stanowisko: row.positionName,
      "Rodzaj umowy": contractPolicy(row.contractPolicy),
      "Stawka brutto/h": row.hourlyRate,
      "Stawka netto/h": row.netHourlyRate,
      "Źródło stawki": rateSource(row.rateSource),
      "Stawka od": row.rateValidFrom,
      "Stawka do": row.rateValidTo,
      "Profil ubezpieczeniowy": insuranceProfile(row.insuranceProfile),
      "Składki pracodawcy %": row.employerContributionPercent,
      "Składki pracodawcy / h": row.employerContributionsHourly,
      "Prowizja agencji %": row.agencyCommissionPercent,
      "Prowizja agencji / h": row.agencyCommissionHourly,
      "Firma/agencja": row.hiringCompanyName,
      Umowa: row.contractIdentifier,
      "Norma miesiąca h": row.monthlyNormHours,
      "Koszt całkowity / h": row.totalHourlyCost,
      "Koszt kompletny": row.costComplete ? "TAK" : "NIE",
      "Plan h": row.plannedHours,
      "Wykonanie h": row.actualHours,
      "Wynagrodzenie netto plan": row.plannedNetWages,
      "Wynagrodzenie netto wykonanie": row.actualNetWages,
      "Wynagrodzenie brutto plan": row.plannedBaseWages,
      "Wynagrodzenie brutto wykonanie": row.actualBaseWages,
      "Koszt pracodawcy plan": row.plannedEmployerCost,
      "Koszt pracodawcy wykonanie": row.actualEmployerCost
    }));
    const locationRows = (report.locations || []).map(row => ({
      Lokalizacja: row.locationName,
      "Sprzedaż wykonana": row.actualSales,
      "Rzeczywiste roboczogodziny RCP": row.actualHours,
      "SPMH wykonanie": row.spmhActual
    }));
    const details = (report.timeEntries || []).map(row => ({
      Typ: row.entryType,
      Data: row.date,
      Pracownik: row.employeeFullName,
      Stanowisko: row.positionName,
      Grafik: row.scheduleName,
      "Plan od": row.plannedStart,
      "Plan do": row.plannedEnd,
      "RCP od": row.actualStart,
      "RCP do": row.actualEnd,
      "Plan min": row.plannedMinutes,
      "Plan rozliczany min": row.accountedPlannedMinutes,
      "Praca min": row.workedMinutes,
      "Przerwa min": row.breakMinutes,
      "Bilans min": row.deltaMinutes,
      "Spóźnienie min": row.lateMinutes,
      "Wcześniejsze wyjście min": row.earlyLeaveMinutes,
      "Ponad grafik min": row.overtimeMinutes,
      "Niepełne dane RCP": row.attendanceDataRisk ? "TAK" : "NIE",
      "Ryzyko braku obsady": row.coverageRisk ? "TAK" : "NIE",
      "Minuty zagrożone": row.coverageRiskMinutes,
      Status: row.attendanceStatus,
      Nieobecność: row.absenceType,
      Źródło: row.source
    }));
    [["Dashboard", dashboardRows], ["Pracownicy", employeeRows], ["Norma miesięczna", monthlyRows], ["Dni", days], ["SPMH lokalizacje", locationRows], ["Stawki", rates], ["Ewidencja", details]].forEach(([name, rows]) => {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
    });
    XLSX.writeFile(wb, `analiza_czasu_${report.departmentName}_${report.dateFrom}_${report.dateTo}.xlsx`);
    toastSuccess("Zapisano raport Excel.");
  };

  const downloadBackendFile = async (endpoint, filename, requireEmployee = false) => {
    if (!report || !departmentId) return;
    if (requireEmployee && !employeeId) {
      toastError("Wybierz pracownika.");
      return;
    }
    setExporting(true);
    try {
      const params = { departmentId, dateFrom, dateTo };
      if (employeeId) params.employeeId = employeeId;
      const response = await axiosPrivate.get(endpoint, { params, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toastSuccess("Plik został wygenerowany.");
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  const selectedEmployee = employees.find(item => String(item.id) === String(employeeId));
  const fileSafeEmployee = (selectedEmployee?.fullName || "pracownik").replace(/[^a-zA-Z0-9ąćęłńóśźżĄĆĘŁŃÓŚŹŻ_-]+/g, "_");

  return (
    <div id="layoutSidenav_content">
      <ToastContainer />
      <div className="app-component-card-header shadow schedule-reports-header">
        <div>
          <div className="my-schedule-title">Analiza czasu pracy i raportowanie</div>
          <div className="my-schedule-subtitle">Grafik i RCP, norma miesięczna, bilans godzin, frekwencja, rozliczenie według stawek, koszty oraz eksport do księgowości i Gang.</div>
        </div>
      </div>

      <div className="container-fluid px-4">
        <div className="schedule-section schedule-reports-toolbar">
          <Form.Group>
            <Form.Label>Dział</Form.Label>
            <Form.Select value={departmentId} onChange={event => setDepartmentId(event.target.value)}>
              {departments.length === 0 && <option value="">Brak dostępnych działów</option>}
              {departments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Pracownik</Form.Label>
            <Form.Select value={employeeId} onChange={event => setEmployeeId(event.target.value)}>
              <option value="">Wszyscy pracownicy</option>
              {employees.map(item => <option key={item.id} value={item.id}>{item.fullName}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group><Form.Label>Od</Form.Label><Form.Control type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} /></Form.Group>
          <Form.Group><Form.Label>Do</Form.Label><Form.Control type="date" value={dateTo} onChange={event => setDateTo(event.target.value)} /></Form.Group>
          <Button variant="outline-secondary" onClick={useTodayRange}>Dzisiaj</Button>
          <Button variant="outline-secondary" onClick={useCurrentMonthRange}>Bieżący miesiąc</Button>
          <Button onClick={() => loadReport()} disabled={loading || !departmentId}>Odśwież raport</Button>
          <Button variant="outline-success" onClick={exportExcel} disabled={!report}>Excel – analiza</Button>
          <Button variant="outline-success" onClick={() => downloadBackendFile("/schedule-reports/export/accounting", `qstaff_raport_ksiegowy_${dateFrom}_${dateTo}.xlsx`)} disabled={!report || exporting}>Raport księgowy XLSX</Button>
          <Button variant="outline-success" onClick={() => downloadBackendFile("/schedule-reports/export/employer", `qstaff_raport_pracodawcy_${dateFrom}_${dateTo}.xlsx`)} disabled={!report || exporting}>Raport pracodawcy XLSX</Button>
          <Button variant="outline-primary" onClick={() => downloadBackendFile("/schedule-reports/export/contractor", `qstaff_rozliczenie_${fileSafeEmployee}_${dateFrom}_${dateTo}.xlsx`, true)} disabled={!report || !employeeId || exporting}>Rozliczenie pracownika XLSX</Button>
          <Button variant="outline-dark" onClick={() => downloadBackendFile("/schedule-reports/export/gang", `qstaff_gang_${employeeId ? fileSafeEmployee : report?.departmentName || "dzial"}_${dateFrom}_${dateTo}.csv`)} disabled={!report || exporting}>{exporting ? "Eksportowanie..." : `Gang – ${employeeId ? "pracownik" : "dział"} CSV`}</Button>
        </div>

        {loading ? <LoadingDataDiv /> : !report ? <div className="my-schedule-empty">Brak danych do wyświetlenia.</div> : <>
          <div className="schedule-report-kpis">
            <Kpi label="Godziny plan / wykonanie" value={`${number(summary.plannedHours)} / ${number(summary.actualHours)} h`} detail={`Bilans względem grafiku ${number(summary.hoursDelta)} h`} />
            <Kpi label="Norma / nadgodziny" value={`${hours(summary.normHours)} / ${hours(summary.overtimeHours)}`} detail={`Niedobór ${hours(summary.underNormHours)} · ponad grafik ${hours(summary.shiftExtraHours)}`} danger={(summary.overtimeHours || 0) > 0} />
            <Kpi label="Frekwencja" value={percent(summary.attendancePercent)} detail={`${summary.lateCount || 0} spóźnień · ${summary.earlyLeaveCount || 0} wcześn. wyjść · ${summary.missingRcpCount || 0} braków startu`} danger={(summary.missingRcpCount || 0) > 0} />
            <Kpi label="Średnia obsada / dzień" value={`${number(averageStaffing.planned)} / ${number(averageStaffing.effective)}`} detail={`plan / efektywna · śr. stawka wykonania netto/brutto ${averageActualNetRate == null ? "—" : `${number(averageActualNetRate)} zł/h`} / ${averageActualGrossRate == null ? "—" : `${number(averageActualGrossRate)} zł/h`}`} danger={(summary.incompleteNetRateAssignmentCount || 0) > 0 || (summary.incompleteGrossRateAssignmentCount || 0) > 0} />
            <Kpi label="Koszt plan / wykonanie" value={`${money(summary.plannedEmployerCost)} / ${money(summary.actualEmployerCost)}`} detail={`Brutto ${money(summary.plannedBaseWages)} / ${money(summary.actualBaseWages)} · netto ${money(summary.plannedNetWages)} / ${money(summary.actualNetWages)} · braki brutto/netto/koszt: ${summary.incompleteGrossRateAssignmentCount || 0}/${summary.incompleteNetRateAssignmentCount || 0}/${summary.incompleteCostAssignmentCount || 0}`} danger={(summary.incompleteCostAssignmentCount || 0) > 0} />
            <Kpi label="Planowane odprawy" value={money(summary.plannedSeveranceCost)} detail={`${summary.severanceContractCount || 0} umów kończących się w zakresie raportu z wpisanym kosztem odprawy`} />
            <Kpi label="Braki kadrowe" value={`${summary.effectiveShortageCount || 0}`} detail={`${summary.openShiftCount || 0} planowanych · ${summary.rcpCoverageRiskCount || 0} incydentów RCP · ${summary.coverageRiskMinutes || 0} min ubytku`} danger={(summary.effectiveShortageCount || 0) > 0 || (summary.coverageRiskMinutes || 0) > 0} />
            <Kpi label="SPMH plan / wykonanie" value={`${number(summary.spmhPlan)} / ${number(summary.spmhActual)}`} detail={`COL ${percent(summary.colPlanPercent)} / ${percent(summary.colActualPercent)}`} />
            <Kpi label="Wnioski" value={`${summary.requestCount || 0}`} detail={`${summary.pendingRequestCount || 0} oczekuje · ${summary.approvedRequestCount || 0} zatwierdzonych`} />
          </div>

          <div className="schedule-report-extremes schedule-section">
            <span><strong>Najmniej godzin:</strong> {summary.minimumHoursEmployee || "—"} ({hours(summary.minimumEmployeeHours)})</span>
            <span><strong>Najwięcej godzin:</strong> {summary.maximumHoursEmployee || "—"} ({hours(summary.maximumEmployeeHours)})</span>
            <span><strong>Najczęściej nieobecny:</strong> {mostAbsentEmployee ? `${mostAbsentEmployee.employeeFullName} (${mostAbsentEmployee.absenceDays || 0} dni)` : "brak nieobecności"}</span>
            <span><strong>Osoby wymagające uwagi:</strong> {problemEmployees.length}</span>
            <span><strong>Filtr:</strong> {selectedEmployee?.fullName || "cały dział"}</span>
          </div>

          <div className="schedule-section schedule-report-tabs">
            <Tabs defaultActiveKey="employees">
              <Tab eventKey="employees" title="Pracownicy">
                <TableWrap><table className="schedule-report-table"><thead><tr><th>Pracownik</th><th>Umowa</th><th>Plan</th><th>Wykonanie</th><th>Plan-wykon.</th><th>Norma</th><th>Do normy</th><th>Nadgodziny</th><th>Ponad grafik</th><th>Frekwencja</th><th>Dni plan/obec.</th><th>Weekendy</th><th>Min/max dni tydz.</th><th>&gt;5 dni</th><th>&lt;3 / &lt;4 dni</th><th>Spóźnienia</th><th>Wcześn. wyj.</th><th>Brak RCP</th><th>Nieobecności</th><th>Koszt plan</th><th>Koszt wykon.</th><th>Odprawy</th><th>Braki kosztu</th></tr></thead><tbody>
                  {(report.employees || []).map(row => <tr key={row.employeeId} className={row.weeksOverFiveDays > 0 || row.weeksUnderFourDays > 0 || row.missingRcpCount > 0 || row.incompleteCostAssignmentCount > 0 ? "schedule-report-warning-row" : ""}><td>{row.employeeFullName}</td><td>{contractPolicy(row.contractPolicy)}</td><td>{hours(row.plannedHours)}</td><td>{hours(row.actualHours)}</td><td>{hours(row.hoursDelta)}</td><td>{hours(row.normHours)}</td><td>{hours(row.hoursToNorm)}</td><td>{hours(row.overtimeHours)}</td><td>{hours(row.shiftExtraHours)}</td><td>{percent(row.attendancePercent)}</td><td>{row.plannedWorkDays || 0}/{row.attendedWorkDays || 0}</td><td>{row.weekendDays}</td><td>{row.minimumDaysInWeek}/{row.maximumDaysInWeek}</td><td>{row.weeksOverFiveDays}</td><td>{row.weeksUnderThreeDays}/{row.weeksUnderFourDays}</td><td>{row.lateCount}</td><td>{row.earlyLeaveCount}</td><td>{row.missingRcpCount}</td><td>{row.absenceDays}</td><td>{money(row.plannedEmployerCost)}</td><td>{money(row.actualEmployerCost)}</td><td>{row.severanceContractCount ? `${money(row.plannedSeveranceCost)} / ${row.severanceContractCount}` : "—"}</td><td>{row.incompleteCostAssignmentCount || 0}</td></tr>)}
                </tbody></table></TableWrap>
              </Tab>

              <Tab eventKey="norm" title="Norma i nadgodziny">
                <div className="schedule-report-gang-note mb-2"><strong>Rozróżnienie:</strong> „Nadgodziny wg normy” to dodatni miesięczny bilans godzin etatowych względem normy. „Ponad grafik” oznacza czas przepracowany po planowanym końcu konkretnych zmian i jest pokazywany osobno.</div>
                <TableWrap><table className="schedule-report-table"><thead><tr><th>Miesiąc</th><th>Pracownik</th><th>Okres</th><th>Pełny miesiąc</th><th>Umowa</th><th>Norma</th><th>Plan</th><th>Wykonanie</th><th>Etat wykon.</th><th>Do normy</th><th>Nadgodziny</th><th>Niedobór</th><th>Ponad grafik</th><th>Dni plan/obec.</th><th>Frekwencja</th><th>Spóźn.</th><th>Wcześn. wyj.</th><th>Brak RCP</th><th>Nieobecności</th></tr></thead><tbody>
                  {(report.monthlyNorms || []).map(row => <tr key={`${row.employeeId}-${row.month}`} className={(row.overtimeHours || 0) > 0 || (row.missingRcpCount || 0) > 0 ? "schedule-report-warning-row" : ""}><td>{row.month}</td><td>{row.employeeFullName}</td><td>{row.periodFrom} – {row.periodTo}</td><td>{row.completeMonth ? "TAK" : "NIE"}</td><td>{contractPolicy(row.contractPolicy)}</td><td>{hours(row.normHours)}</td><td>{hours(row.plannedHours)}</td><td>{hours(row.actualHours)}</td><td>{hours(row.employmentActualHours)}</td><td>{hours(row.hoursToNorm)}</td><td>{hours(row.overtimeHours)}</td><td>{hours(row.underNormHours)}</td><td>{hours(row.shiftExtraHours)}</td><td>{row.plannedWorkDays || 0}/{row.attendedWorkDays || 0}</td><td>{percent(row.attendancePercent)}</td><td>{row.lateCount || 0}</td><td>{row.earlyLeaveCount || 0}</td><td>{row.missingRcpCount || 0}</td><td>{row.absenceDays || 0}</td></tr>)}
                </tbody></table></TableWrap>
              </Tab>

              <Tab eventKey="days" title="Dni / wnioski / obsada">
                <div className="schedule-report-gang-note mb-2"><strong>Bilans dnia:</strong> wykonanie minus plan. Zgodnie z wymaganiem dashboardu wartość ujemna jest oznaczona na zielono, a dodatnia na czerwono.</div>
                <TableWrap><table className="schedule-report-table"><thead><tr><th>Data</th><th>Zapotrzebowanie</th><th>Obsada plan</th><th>Obsada efektywna</th><th>Braki plan</th><th>Ryzyko RCP</th><th>Braki efektywne</th><th>Min zagrożone</th><th>Spóźnienia</th><th>Wcześn. wyj.</th><th>Brak startu</th><th>Wnioski</th><th>Wolne</th><th>Urlopy</th><th>Otwarte</th><th>Zamiany</th><th>Łączne plan h</th><th>Łączne wykon. h</th><th>Bilans dnia</th><th>Sprzedaż plan</th><th>Sprzedaż wykon.</th><th>SPMH plan</th><th>SPMH wykon.</th><th>Braki kosztu</th></tr></thead><tbody>
                  {(report.days || []).map(row => {
                    const dayBalance = Number(row.actualHours || 0) - Number(row.plannedHours || 0);
                    return <tr key={row.date} className={(row.effectiveShortageCount || 0) > 0 ? "schedule-report-danger-row" : ""}><td>{row.date}</td><td>{row.demandCount}</td><td>{row.assignedCount}</td><td>{row.effectiveAssignedCount}</td><td>{row.openShiftCount}</td><td>{row.rcpCoverageRiskCount}</td><td>{row.effectiveShortageCount}</td><td>{row.coverageRiskMinutes}</td><td>{row.lateCount}</td><td>{row.earlyLeaveCount}</td><td>{row.missingRcpCount}</td><td>{row.requestCount}</td><td>{row.dayOffRequests}</td><td>{row.vacationRequests}</td><td>{row.openShiftRequests}</td><td>{row.swapRequests}</td><td>{number(row.plannedHours)}</td><td>{number(row.actualHours)}</td><td className={balanceClass(dayBalance)}>{signedHours(dayBalance)}</td><td>{money(row.plannedSales)}</td><td>{money(row.actualSales)}</td><td>{number(row.spmhPlan)}</td><td>{number(row.spmhActual)}</td><td>{row.incompleteCostAssignmentCount || 0}</td></tr>;
                  })}
                </tbody></table></TableWrap>
              </Tab>

              <Tab eventKey="locations" title="SPMH wg lokalizacji">
                {employeeId ? <div className="schedule-report-gang-note">Widok SPMH lokalizacji jest dostępny dla całego działu, ponieważ sprzedaż lokalizacji nie jest przypisywana do pojedynczego pracownika.</div> : <LocationSalesPanel departmentId={Number(departmentId)} dateFrom={dateFrom} dateTo={dateTo} reportLocations={report.locations || []} onChanged={() => loadReport(dateFrom, dateTo, employeeId)} />}
              </Tab>

              <Tab eventKey="employer" title="Raport pracodawcy">
                <div className="schedule-report-gang-note mb-2"><strong>Raport pracodawcy:</strong> roboczogodziny są oparte na wykonaniu RCP. Stawka netto jest ewidencjonowana niezależnie od brutto i nie jest zgadywana. Jeśli dla części czasu brakuje stawki netto lub pełnej kalkulacji kosztu, wartości są oznaczone jako niepełne.</div>
                <TableWrap><table className="schedule-report-table"><thead><tr><th>Pracownik</th><th>Roboczogodziny</th><th>Stawka netto/h</th><th>Stawka brutto/h</th><th>Wynagrodzenie netto</th><th>Wynagrodzenie brutto</th><th>Całkowity koszt zatrudnienia</th><th>Status netto</th><th>Status brutto</th><th>Status kosztu</th></tr></thead><tbody>
                  {(report.employees || []).map(row => {
                    const netComplete = (row.incompleteNetRateAssignmentCount || 0) === 0;
                    const grossComplete = (row.incompleteGrossRateAssignmentCount || 0) === 0;
                    const costComplete = (row.incompleteCostAssignmentCount || 0) === 0;
                    return <tr key={`employer-${row.employeeId}`} className={!netComplete || !grossComplete || !costComplete ? "schedule-report-warning-row" : ""}><td>{row.employeeFullName}</td><td>{hours(row.actualHours)}</td><td>{netComplete ? money(row.effectiveNetHourlyRate) : "—"}</td><td>{grossComplete ? money(row.effectiveGrossHourlyRate) : "—"}</td><td>{netComplete ? money(row.actualNetWages) : "—"}</td><td>{grossComplete ? money(row.actualBaseWages) : "—"}</td><td>{costComplete ? money(row.actualEmployerCost) : "—"}</td><td>{netComplete ? "KOMPLETNA" : `BRAK (${row.incompleteNetRateAssignmentCount || 0})`}</td><td>{grossComplete ? "KOMPLETNA" : `BRAK (${row.incompleteGrossRateAssignmentCount || 0})`}</td><td>{costComplete ? "KOMPLETNY" : `BRAK (${row.incompleteCostAssignmentCount || 0})`}</td></tr>;
                  })}
                </tbody></table></TableWrap>
              </Tab>

              <Tab eventKey="rates" title="Godziny i koszty wg stawek">
                <TableWrap><table className="schedule-report-table"><thead><tr><th>Pracownik</th><th>Stanowisko</th><th>Umowa</th><th>Brutto/h</th><th>Netto/h</th><th>Źródło</th><th>Okres stawki</th><th>Profil</th><th>Składki % / h</th><th>Agencja % / h</th><th>Firma/agencja</th><th>Dokument umowy</th><th>Koszt/h</th><th>Kompletny</th><th>Plan h</th><th>Wykon. h</th><th>Wynagr. plan</th><th>Wynagr. wykon.</th><th>Koszt plan</th><th>Koszt wykon.</th></tr></thead><tbody>
                  {(report.rates || []).map((row, index) => <tr key={`${row.employeeId}-${row.positionId}-${row.hourlyRate}-${row.contractIdentifier || ""}-${index}`}><td>{row.employeeFullName}</td><td>{row.positionName || "—"}</td><td>{contractPolicy(row.contractPolicy)}</td><td>{money(row.hourlyRate)}</td><td>{money(row.netHourlyRate)}</td><td>{rateSource(row.rateSource)}</td><td>{row.rateValidFrom || "—"} – {row.rateValidTo || "bez końca"}</td><td>{insuranceProfile(row.insuranceProfile)}</td><td>{percent(row.employerContributionPercent)} / {money(row.employerContributionsHourly)}</td><td>{percent(row.agencyCommissionPercent)} / {money(row.agencyCommissionHourly)}</td><td>{row.hiringCompanyName || "—"}</td><td>{row.contractIdentifier || "—"}{row.monthlyNormHours != null ? ` / norma ${number(row.monthlyNormHours)} h` : ""}</td><td>{money(row.totalHourlyCost)}</td><td>{row.costComplete ? "TAK" : "NIE"}</td><td>{hours(row.plannedHours)}</td><td>{hours(row.actualHours)}</td><td>{money(row.plannedBaseWages)}</td><td>{money(row.actualBaseWages)}</td><td>{money(row.plannedEmployerCost)}</td><td>{money(row.actualEmployerCost)}</td></tr>)}
                </tbody></table></TableWrap>
              </Tab>

              <Tab eventKey="time" title="Szczegółowa ewidencja czasu">
                <TableWrap><table className="schedule-report-table"><thead><tr><th>Typ</th><th>Data</th><th>Pracownik</th><th>Stanowisko / nieobecność</th><th>Plan</th><th>RCP</th><th>Praca</th><th>Przerwa</th><th>Bilans</th><th>Spóźnienie</th><th>Wcześn. wyjście</th><th>Ponad grafik</th><th>Status</th><th>Źródło</th></tr></thead><tbody>
                  {(report.timeEntries || []).map((row, index) => <tr key={`${row.entryType}-${row.assignmentId || index}-${row.date}`}><td>{entryType(row.entryType)}</td><td>{row.date}</td><td>{row.employeeFullName || "—"}</td><td>{row.positionName || row.absenceType || "—"}</td><td>{row.plannedStart ? `${dateTime(row.plannedStart)} – ${dateTime(row.plannedEnd)}` : "—"}</td><td>{row.actualStart ? `${dateTime(row.actualStart)} – ${dateTime(row.actualEnd)}` : "—"}</td><td>{row.workedMinutes ?? "—"} min</td><td>{row.breakMinutes ?? "—"} min</td><td>{row.deltaMinutes ?? "—"} min</td><td>{row.lateMinutes ?? "—"}</td><td>{row.earlyLeaveMinutes ?? "—"}</td><td>{row.overtimeMinutes ?? "—"} min</td><td>{row.attendanceStatus || "—"}</td><td>{row.source || "—"}</td></tr>)}
                </tbody></table></TableWrap>
              </Tab>
            </Tabs>
          </div>

          <div className="schedule-section schedule-report-gang-note">
            <strong>Eksport Gang:</strong> CSV można wygenerować zbiorczo dla działu albo dla wybranego pracownika. Kodowanie, separator, nagłówek, format dat/czasu i kolejność/nazwy kolumn są konfigurowane w Ustawieniach globalnych. Po otrzymaniu profilu importowego księgowości należy wpisać dokładnie jego mapowanie i wykonać próbny import w docelowej instalacji Wapro Gang.
          </div>
        </>}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, detail, danger }) => <div className={`schedule-report-kpi ${danger ? "danger" : ""}`}><small>{label}</small><strong>{value}</strong><span>{detail}</span></div>;
const TableWrap = ({ children }) => <div className="schedule-report-table-wrap">{children}</div>;
const entryType = value => ({ SHIFT: "Zmiana", OPEN_SHIFT: "Brak obsady", RCP_ONLY: "RCP bez grafiku", ABSENCE: "Nieobecność" }[value] || value);

export default ScheduleManagerReports;

